import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'wouter'
import { Button } from '@/components/ui/button'
import {
	Receipt,
	House,
	CreditCard,
	Clock,
	Menu,
	HandCoins,
	X,
	Bell,
	RefreshCcwDot,
	Newspaper,
	Image,
	CalendarDays,
	Shirt
} from 'lucide-react'
import { useLoading } from './LoadingProvider'
import { ErrorBoundary } from './ErrorBoundary'
import { useMobile } from '../hooks/use-mobile'
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogTrigger
} from '@/components/ui/dialog'
import logoUrl from '@/assets/logo.png'
import { cn } from '@/lib/utils'
import { VisuallyHidden } from '@/components/ui/visually-hidden'
import { signOut } from 'firebase/auth'
import { auth } from '@/constants'
import { useUser } from '@/hooks/use-user'
import { useNotificationContext } from '@/providers/NotificationProvider'
import ConfirmModal from '@/components/common/ConfirmationModal'
import React from 'react'
interface HeaderProps {
	showNavigation?: boolean
	showRefreshButton?: boolean
}

export default function Header({ showNavigation = true, showRefreshButton= true }: HeaderProps) {
	const {
		isConnected,
		disconnectWallet,
		setUserData
	} = useUser()

	const [location, navigate] = useLocation()
	const { showLoader } = useLoading()
	const { isMobile, isMenuOpen, toggleMenu, closeMenu } = useMobile()
	const isMounted = useRef(true)
	const menuButtonRef = useRef<HTMLButtonElement>(null)
	const menuRef = useRef<HTMLDivElement>(null)
	const [showConfirmModal, setShowConfirmModal] = useState(false)
	const [showExternalLinkModal, setShowExternalLinkModal] = useState(false)
	const { unreadCount } = useNotificationContext()

	// Log version on component mount
	useEffect(() => {
		const version = import.meta.env.VITE_APP_VERSION || 'dev'
		console.log(`App Version: ${version}`)
	}, [])

	const handleNavigation = useCallback(
		(event: MouseEvent) => {
			const target = event.target as HTMLElement
			const link = target.closest('a')

			if (link?.getAttribute('href')?.startsWith('/')) {
				const href = link.getAttribute('href')!

				if (href === location) {
					event.preventDefault()
					return
				}

				showLoader(800)
				closeMenu()
			}
		},
		[location, showLoader, closeMenu]
	)

	const handleLogOut = async () => {
		// Reset cookie to prevent re-login after sign out
		document.cookie = `session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
		await signOut(auth)

		setUserData(null)
		if (isConnected) {
			await disconnectWallet()
		}
		navigate('/login')
	}

	useEffect(() => {
		isMounted.current = true
		const nav = document.querySelector('nav')

		if (nav) {
			nav.addEventListener('click', handleNavigation)
		}

		const handleBackButton = () => {
			if (isMenuOpen) {
				closeMenu()
			}
		}

		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && isMenuOpen) {
				closeMenu()
				menuButtonRef.current?.focus()
			}
		}

		window.addEventListener('popstate', handleBackButton)
		document.addEventListener('keydown', handleEscape)

		return () => {
			isMounted.current = false
			if (nav) {
				nav.removeEventListener('click', handleNavigation)
			}
			window.removeEventListener('popstate', handleBackButton)
			document.removeEventListener('keydown', handleEscape)
		}
	}, [handleNavigation, isMenuOpen, closeMenu])

	useEffect(() => {
		const handleFocus = () => {
			if (!menuRef.current) return

			// Find buttons inside the dialog
			const buttons = Array.from(
				menuRef.current.querySelectorAll<HTMLButtonElement>('button')
			)

			// Find the active button
			const activeButton = buttons.find(
				(button) =>
					button?.innerText?.trim()?.toLowerCase() ===
					NavigationLinks.find(
						(link) => link.href === location
					)?.label?.toLowerCase()
			)

			// Focus the active button
			activeButton?.focus()
		}

		setTimeout(() => {
			if (isMenuOpen) {
				handleFocus()
			}
		}, 0)
	}, [menuRef.current, location, isMenuOpen])

	// Regular navigation links (available to all users)
	const NavigationLinks = useMemo(() => {
		return [
			{ href: '/', icon: House, label: 'Wallet' },
			{ href: '/membership', icon: CreditCard, label: 'Membership' },
			{ href: '/entry-passes', icon: Clock, label: 'Entry Passes' },
			{ href: '/reservations', icon: CalendarDays, label: 'Reservations' },
			{ href: '/buy-tokens', icon: HandCoins, label: 'Buy Tokens' },
			{ href: '/transactions', icon: Receipt, label: 'Transactions' },
			{ href: '/news', icon: Newspaper, label: 'News' },
			{ href: '/galleries', icon: Image, label: 'Gallery' },
			{ href: 'https://www.secretssociety.com/', icon: Shirt, label: 'Merch', external: true }
		]
	}, [])

	const NavigationContent = () => (
		<nav
			className={cn(
				'flex items-center',
				isMobile ? 'flex-col w-full space-y-1 p-6 pt-0' : 'hidden md:flex md:gap-1'
			)}>
			{NavigationLinks.map(({ href, icon: Icon, label, external }) => {
				const isActive = !external && location === href
				return (
					<React.Fragment key={href}>
					<Button
						variant="ghost"
						size={isMobile ? 'lg' : 'sm'}
						className={cn(
							'w-full justify-start transition-colors',
							isMobile
								? 'h-16  px-4 text-white hover:bg-[#2F2F32] focus:bg-[#2F2F32] rounded-lg leading-4 font-medium font-figtree text-sm'
								: 'h-9 text-white/70 hover:text-white hover:bg-[#2F2F32] focus:bg-accent focus:text-accent-foreground rounded-lg',
							isActive &&
							(isMobile
								? 'bg-[#2F2F32] text-white'
								: 'bg-[#2F2F32] !text-white shadow-sm')
						)}
						onClick={() => {
							if (external) {
								setShowExternalLinkModal(true)
								return
							}
							navigate(href)
							if(isMobile) closeMenu()
						}}>
						<div className="flex items-center gap-4">
							<Icon className={cn('shrink-0', isMobile ? 'h-[22px] w-[22px]' : 'h-[22px] w-[22px]')}/>
							<span className="truncate font-figtree text-sm leading-4 font-medium text-white">{label}</span>
						</div>
					</Button>
					<div className={cn("h-[1px] bg-[#2F2F32] w-full m-0 p-0", isActive ? 'hidden' : 'block')} />
					</React.Fragment>
				)
			})}

			</nav>
	)

	return (
		<ErrorBoundary>
			<header className="px-5 py-2 bg-transparent h-[54px] flex justify-between items-center">

				{showNavigation && isMobile && (
					<Dialog open={isMenuOpen} onOpenChange={toggleMenu}>
						<DialogTrigger asChild>
							<Button
								ref={menuButtonRef}
								variant="ghost"
								size="icon"
								className="!h-[42px] !w-[22px] flex items-center hover:bg-transparent hover:text-inherit focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 focus:ring-offset-0 focus-visible:ring-offset-0 focus-visible:shadow-none"
								aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
								aria-expanded={isMenuOpen}
								aria-controls="mobile-menu">
								{isMenuOpen ? (
									<X className="h-5 w-5" />
								) : (
									<Menu className="h-[22px] w-[22px] text-[#ffffff]" />
								)}
								<VisuallyHidden className=''>
									{isMenuOpen ? 'Close menu' : 'Open menu'}
								</VisuallyHidden>
							</Button>
						</DialogTrigger>
						<DialogContent
							ref={menuRef}
							id="mobile-menu"
							className="mobile-menu-panel bg-[#18181B] text-white border-[#2F2F32] p-0 m-0 h-[100dvh] w-screen max-w-none max-h-none rounded-none fixed !left-0 !top-0 !translate-x-0 !translate-y-0"
							onCloseAutoFocus={() => menuButtonRef.current?.focus()}
							onInteractOutside={(e) => {
								// Only close on explicit tap of backdrop
								if (e.type === 'touchend' && e.target === e.currentTarget) {
									closeMenu()
								} else if (e.type === 'touchend') {
									e.preventDefault()
								}
							}}
							onClick={closeMenu}>
							<div className="flex flex-col h-full overflow-hidden">
								<div className="flex-shrink-0 flex items-center justify-between p-6 bg-[#18181B]">
									<DialogTitle className="text-xl font-semibold text-white">Navigation Menu</DialogTitle>
									<Button
										variant="ghost"
										size="icon"
										className="text-white hover:bg-white/10 h-[22px] w-[22px] rounded-full"
										onClick={closeMenu}
										aria-label="Close menu">
										<X className="h-[22px] w-[22px]" />
									</Button>
								</div>
								<div className="flex-1 min-h-0 overflow-y-auto bg-[#18181B] pb-20" onClick={(e) => e.stopPropagation()}>
									<NavigationContent />
								</div>
							</div>
						</DialogContent>
					</Dialog>
				)}

				<div className="flex items-center gap-3">
					<Link href="/" className="flex items-center gap-1">
						<img
							src={logoUrl}
							alt="App Logo"
							rel="preload"
							className="h-[30px] w-[30px] rounded-full"
						/>
						<span className="flex flex-col justify-between">
							<strong className="text-sm leading-4 font-bold font-montserrat text-[#ffffff]">SECH</strong>
							<small className="text-[8px] font-medium leading-[10px] font-montserrat text-[#ffffff]">Digital Wallet</small>
						</span>
					</Link>
					</div>

				{showNavigation && !isMobile && <NavigationContent />}

				<div className="flex items-center gap-2">
					{/* Refresh Button */}
					{showRefreshButton && (
						<Button
							className="h-[42px] w-[42px] flex items-center justify-center rounded-full bg-[rgba(145,145,145,0.19)]
							hover:bg-transparent hover:text-inherit focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 focus:ring-offset-0 focus-visible:ring-offset-0 focus-visible:shadow-none"
							aria-label="Refresh"
							onClick={() => window.location.reload()}>
							<RefreshCcwDot className="h-[22px] w-[22px] text-[#ffffff]" />
						</Button>
					)}
					{/* Notification Bell */}
					<Button
						variant="ghost"
						size="icon"
						className="relative h-[42px] w-[42px] flex items-center justify-center rounded-full bg-[rgba(145,145,145,0.19)]
						hover:bg-transparent hover:text-inherit focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 focus:ring-offset-0 focus-visible:ring-offset-0 focus-visible:shadow-none"
						aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
						onClick={() => navigate('/notifications')}>
						<Bell className="h-[22px] w-[22px] text-[#ffffff]" />
						{unreadCount > 0 && (
							<span className="absolute -top-1 -right-1 h-5 w-5 min-w-5 rounded-full bg-red-500 text-white text-xs font-medium flex items-center justify-center">
								{unreadCount > 99 ? '99+' : unreadCount}
							</span>
						)}
					</Button>
				</div>


			</header>
			<ConfirmModal
				open={showConfirmModal}
				onClose={() => setShowConfirmModal(false)}
				onConfirm={handleLogOut}
				title="Log Out"
				description="Are you sure you want to log out?"
				confirmText="Log Out"
				cancelText="Cancel"
				variant="logout"
			/>
			<ConfirmModal
				open={showExternalLinkModal}
				onClose={() => setShowExternalLinkModal(false)}
				onConfirm={() => {
					setShowExternalLinkModal(false)
					closeMenu()
					const url = 'https://www.secretssociety.com/'
					if (window.Android?.onRedirectDetected) {
						window.Android.onRedirectDetected(url)
					}
					window.location.href = url
				}}
				title="You're leaving the app"
				description="You will be redirected to the Secrets Society website to browse merchandise. The external site's terms and policies will apply."
				confirmText="Continue to Merch"
				cancelText="Stay in SECH"
			/>
		</ErrorBoundary>
	)
}